import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, createBlock as _createBlock, withCtx as _withCtx } from "vue"

const _hoisted_1 = {
  class: "flex flex-col gap-3",
  "data-testid": "ds-attendance-queue-views"
}
const _hoisted_2 = { class: "flex flex-wrap items-end gap-4" }
const _hoisted_3 = { class: "flex flex-col gap-1 text-left" }
const _hoisted_4 = /*#__PURE__*/_createElementVNode("span", { class: "text-sm font-bold text-textPrimary" }, "Visão", -1)
const _hoisted_5 = {
  key: 0,
  class: "flex flex-col gap-1 text-left"
}
const _hoisted_6 = /*#__PURE__*/_createElementVNode("span", { class: "text-sm font-bold text-textPrimary" }, "Agrupar por", -1)
const _hoisted_7 = {
  role: "group",
  "aria-label": "Agrupar por",
  class: "inline-flex gap-1 rounded-lg border border-lightGray30 bg-white p-1",
  "data-testid": "ds-attendance-group-by"
}
const _hoisted_8 = ["aria-pressed", "data-testid", "onClick"]
const _hoisted_9 = {
  key: 0,
  class: "flex flex-col gap-2",
  "data-testid": "ds-attendance-queue-loading"
}
const _hoisted_10 = {
  key: 3,
  class: "flex flex-col gap-2",
  "data-testid": "ds-attendance-view-list"
}
const _hoisted_11 = {
  key: 4,
  class: "flex flex-col gap-2",
  "data-testid": "ds-attendance-view-table"
}
const _hoisted_12 = { class: "font-bold text-primary30" }
const _hoisted_13 = { class: "block" }
const _hoisted_14 = { class: "block text-xs text-neutral50" }
const _hoisted_15 = { class: "block" }
const _hoisted_16 = { class: "block text-xs text-neutral50" }
const _hoisted_17 = { key: 1 }
const _hoisted_18 = ["data-testid", "onClick"]
const _hoisted_19 = {
  key: 5,
  "data-testid": "ds-attendance-view-kanban"
}

import { computed } from 'vue'

import EmptyList from '@/components/EmptyList.vue'
import StatusBadge from '@/components/designSystem/Badges/StatusBadge.vue'
import ReportErrorState from '@/components/designSystem/ErrorState/ReportErrorState.vue'
import AuditoriumExceptionalitiesCardSkeleton from '@/components/designSystem/Skeleton/AuditoriumExceptionalitiesCardSkeleton.vue'
import ModuleViewSlaChip from '@/components/designSystem/QueueViews/ModuleViewSlaChip.vue'
import QueueBulkBar from '@/components/designSystem/QueueViews/QueueBulkBar.vue'
import QueueKanban from '@/components/designSystem/QueueViews/QueueKanban.vue'
import QueueTable from '@/components/designSystem/QueueViews/QueueTable.vue'
import QueueViewSwitcher from '@/components/designSystem/QueueViews/QueueViewSwitcher.vue'
import type { QueueSort, QueueViewMode } from '@/components/designSystem/QueueViews/types'
import { DS_ATTENDANCE_QUEUE_SCREEN_KEY } from '@/constants/ds-attendance-sla.constants'
import type { DSAttendanceContainer } from '@/types/ds-attendance.types'

import type { DsAttendanceGroupBy } from '../../composables/useDsAttendanceQueueFilters'
import { toAttendanceListItem } from '../../utils/attendance-list-item'
import { isBulkApproveEligible } from '../../utils/bulk-approve'
import type { DsAttendanceKanbanColumn } from '../../utils/queue-kanban-columns'
import { formatTableDeadline } from '../../utils/queue-sla-line'
import { DS_ATTENDANCE_TABLE_COLUMNS } from '../../utils/queue-table-columns'
import DsAttendanceListCard from '../DsAttendanceListCard.vue'
import DsAttendanceKanbanCard from './DsAttendanceKanbanCard.vue'
import DsAttendanceSlaLine from './DsAttendanceSlaLine.vue'

/**
 * As três visões de Registro de plantão (spec 2026-10-01 §7.1/§7.2): switcher + "Agrupar por",
 * Lista (cartões de hoje + faixa de SLA, G2-D19), Tabela com seleção em lote (só aprováveis) e
 * Kanban K1/K2 com "Ver mais" por coluna. Sem estado próprio: tudo entra por prop e sai por
 * evento — quem busca, pagina e aprova é o `Index.vue`.
 */
const KANBAN_ERROR_MESSAGE = 'Verifique sua conexão e tente novamente.'

/** Mockup "vazio": o recorte carregou e as colunas somam 0. */

export default /*#__PURE__*/_defineComponent({
  __name: 'DsAttendanceQueueViews',
  props: {
    view: {},
    groupBy: {},
    listRows: {},
    kanbanColumns: {},
    kanbanRows: {},
    kanbanTotals: {},
    kanbanHasMore: {},
    kanbanLoading: {},
    kanbanInitialLoading: { type: Boolean, default: false },
    kanbanFailed: { type: Boolean, default: false },
    nowMs: {},
    loading: { type: Boolean },
    error: {},
    emptyTitle: {},
    selectedIds: {},
    deadlineSorted: { type: Boolean },
    skeletonCount: { default: 10 }
  },
  emits: ["update:view", "update:groupBy", "update:selectedIds", "load-more", "details", "retry", "sort-by-deadline", "bulk-approve"],
  setup(__props: any, { emit }) {

const props = __props;





const GROUP_BY_OPTIONS: Array<{ value: DsAttendanceGroupBy; label: string }> = [
  { value: 'estado', label: 'Estado do prazo' },
  { value: 'etapa', label: 'Etapa' }
]

const DEADLINE_SORT: QueueSort = { key: 'deadline', direction: 'asc' }

const kanbanEmpty = computed(() => {
  const totals = Object.values(props.kanbanTotals)
  return (
    totals.length > 0 &&
    totals.reduce<number>((sum, total) => sum + (total ?? 0), 0) === 0 &&
    props.kanbanRows.length === 0
  )
})

const rowKey = (row: DSAttendanceContainer): number => row.id

const listEntries = computed(() =>
  props.listRows.map((container) => ({ container, item: toAttendanceListItem(container) }))
)

/** "Prazo" da Tabela calculado uma vez por linha (a célula lê data e fase). */
const tableDeadlines = computed(
  () => new Map(props.listRows.map((row) => [row.id, formatTableDeadline(row)]))
)

/** Aprováveis em lote, por id — reaproveita o item já mapeado em `listEntries`. */
const selectableIds = computed(
  () =>
    new Set(
      listEntries.value
        .filter((entry) => isBulkApproveEligible(entry.item))
        .map((entry) => entry.container.id)
    )
)

const isRowSelectable = (row: DSAttendanceContainer): boolean => selectableIds.value.has(row.id)

function onSort(sort: QueueSort): void {
  if (sort.key === 'deadline') emit('sort-by-deadline')
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _hoisted_4,
        _createVNode(QueueViewSwitcher, {
          "model-value": _ctx.view,
          "screen-key": _unref(DS_ATTENDANCE_QUEUE_SCREEN_KEY),
          "restore-preference": false,
          "test-id": "ds-attendance-view-switcher",
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (emit('update:view', $event)))
        }, null, 8, ["model-value", "screen-key"])
      ]),
      (_ctx.view === 'kanban')
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
            _hoisted_6,
            _createElementVNode("div", _hoisted_7, [
              (_openBlock(), _createElementBlock(_Fragment, null, _renderList(GROUP_BY_OPTIONS, (option) => {
                return _createElementVNode("button", {
                  key: option.value,
                  type: "button",
                  class: _normalizeClass(["rounded-md px-3.5 py-1.5 text-sm font-medium", 
              option.value === _ctx.groupBy
                ? 'bg-primary5 text-primary'
                : 'text-neutral50 hover:text-textPrimary'
            ]),
                  "aria-pressed": option.value === _ctx.groupBy,
                  "data-testid": `ds-attendance-group-by-${option.value}`,
                  onClick: ($event: any) => (emit('update:groupBy', option.value))
                }, _toDisplayString(option.label), 11, _hoisted_8)
              }), 64))
            ])
          ]))
        : _createCommentVNode("", true)
    ]),
    (_ctx.view === 'kanban' ? _ctx.kanbanInitialLoading : _ctx.loading)
      ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.skeletonCount, (n) => {
            return (_openBlock(), _createBlock(AuditoriumExceptionalitiesCardSkeleton, {
              key: `skeleton-${n}`
            }))
          }), 128))
        ]))
      : (_ctx.view === 'kanban' ? _ctx.kanbanFailed : _ctx.error)
        ? (_openBlock(), _createBlock(ReportErrorState, {
            key: 1,
            title: "Não foi possível carregar os registros de presença",
            message: _ctx.view === 'kanban' ? KANBAN_ERROR_MESSAGE : _ctx.error,
            onRetry: _cache[1] || (_cache[1] = ($event: any) => (emit('retry')))
          }, null, 8, ["message"]))
        : (_ctx.view === 'kanban' && kanbanEmpty.value)
          ? (_openBlock(), _createBlock(EmptyList, {
              key: 2,
              title: _ctx.emptyTitle
            }, null, 8, ["title"]))
          : (_ctx.view === 'list')
            ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                (listEntries.value.length)
                  ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(listEntries.value, (entry) => {
                      return (_openBlock(), _createElementBlock("div", {
                        key: entry.container.id,
                        class: "flex flex-col gap-1"
                      }, [
                        _createVNode(DsAttendanceSlaLine, {
                          container: entry.container,
                          "now-ms": _ctx.nowMs
                        }, null, 8, ["container", "now-ms"]),
                        _createVNode(DsAttendanceListCard, {
                          item: entry.item,
                          onDetails: _cache[2] || (_cache[2] = ($event: any) => (emit('details', $event)))
                        }, null, 8, ["item"])
                      ]))
                    }), 128))
                  : (_openBlock(), _createBlock(EmptyList, {
                      key: 1,
                      title: _ctx.emptyTitle
                    }, null, 8, ["title"]))
              ]))
            : (_ctx.view === 'table')
              ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                  (_ctx.listRows.length)
                    ? (_openBlock(), _createBlock(QueueTable, {
                        key: 0,
                        rows: _ctx.listRows,
                        columns: _unref(DS_ATTENDANCE_TABLE_COLUMNS),
                        "row-key": rowKey,
                        selectable: "",
                        "selected-ids": _ctx.selectedIds,
                        "is-row-selectable": isRowSelectable,
                        sort: _ctx.deadlineSorted ? DEADLINE_SORT : null,
                        "onUpdate:selectedIds": _cache[3] || (_cache[3] = ($event: any) => (emit('update:selectedIds', $event))),
                        "onUpdate:sort": onSort
                      }, {
                        "cell-id": _withCtx(({ row }) => [
                          _createElementVNode("span", _hoisted_12, "#" + _toDisplayString(row.id), 1)
                        ]),
                        "cell-schedule": _withCtx(({ row }) => [
                          _createElementVNode("span", _hoisted_13, _toDisplayString(row.schedule?.name ?? 'Escala não informada'), 1),
                          _createElementVNode("span", _hoisted_14, _toDisplayString(row.healthUnit?.name ?? 'Unidade não informada'), 1)
                        ]),
                        "cell-status": _withCtx(({ row }) => [
                          _createVNode(StatusBadge, {
                            status: row.status
                          }, null, 8, ["status"])
                        ]),
                        "cell-deadline": _withCtx(({ row }) => [
                          (tableDeadlines.value.get(row.id))
                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                _createElementVNode("span", _hoisted_15, _toDisplayString(tableDeadlines.value.get(row.id)?.date), 1),
                                _createElementVNode("span", _hoisted_16, _toDisplayString(tableDeadlines.value.get(row.id)?.phase), 1)
                              ], 64))
                            : (_openBlock(), _createElementBlock("span", _hoisted_17, "—"))
                        ]),
                        "cell-slaState": _withCtx(({ row }) => [
                          _createVNode(ModuleViewSlaChip, {
                            sla: row.sla ?? null,
                            "now-ms": _ctx.nowMs
                          }, null, 8, ["sla", "now-ms"])
                        ]),
                        actions: _withCtx(({ row }) => [
                          _createElementVNode("button", {
                            type: "button",
                            class: "rounded border border-primary px-2.5 py-1 text-xs font-bold text-primary",
                            "data-testid": `ds-attendance-table-details-${row.id}`,
                            onClick: ($event: any) => (emit('details', row.id))
                          }, " Ver detalhes ", 8, _hoisted_18)
                        ]),
                        _: 1
                      }, 8, ["rows", "columns", "selected-ids", "sort"]))
                    : (_openBlock(), _createBlock(EmptyList, {
                        key: 1,
                        title: _ctx.emptyTitle
                      }, null, 8, ["title"])),
                  (_ctx.selectedIds.length)
                    ? (_openBlock(), _createBlock(QueueBulkBar, {
                        key: 2,
                        "selected-count": _ctx.selectedIds.length,
                        "pending-count": 0,
                        "pending-label": "",
                        "primary-label": `Aprovar selecionados (${_ctx.selectedIds.length})`,
                        onPrimary: _cache[4] || (_cache[4] = ($event: any) => (emit('bulk-approve'))),
                        onClear: _cache[5] || (_cache[5] = ($event: any) => (emit('update:selectedIds', [])))
                      }, null, 8, ["selected-count", "primary-label"]))
                    : _createCommentVNode("", true)
                ]))
              : (_openBlock(), _createElementBlock("div", _hoisted_19, [
                  _createVNode(QueueKanban, {
                    rows: _ctx.kanbanRows,
                    columns: _ctx.kanbanColumns,
                    "row-key": rowKey,
                    "column-totals": _ctx.kanbanTotals,
                    "column-has-more": _ctx.kanbanHasMore,
                    "column-loading": _ctx.kanbanLoading,
                    onLoadMore: _cache[7] || (_cache[7] = ($event: any) => (emit('load-more', $event)))
                  }, {
                    card: _withCtx(({ row }) => [
                      _createVNode(DsAttendanceKanbanCard, {
                        container: row,
                        "now-ms": _ctx.nowMs,
                        onDetails: _cache[6] || (_cache[6] = ($event: any) => (emit('details', $event)))
                      }, null, 8, ["container", "now-ms"])
                    ]),
                    _: 1
                  }, 8, ["rows", "columns", "column-totals", "column-has-more", "column-loading"])
                ]))
  ]))
}
}

})